import { useState, useEffect } from 'react';
import { Outlet, NavLink, useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { supabase } from '@/lib/supabase';
import { clearPermissionCache, useHasPermission, PERM } from '@/lib/permissions';
import { clearYardState } from '@/lib/yardStore';
import YardSwitcher from '@/components/layout/YardSwitcher';
import { cn } from '@/lib/utils';
import { LogOut, Languages, Menu, X } from 'lucide-react';

const NAV = [
  { to: '/dashboard', key: 'nav.dashboard', perm: null },
  { to: '/customers', key: 'nav.customers', perm: PERM.CUSTOMER_VIEW },
  { to: '/sales', key: 'nav.sales', perm: PERM.CART_CREATE },
  { to: '/cashier', key: 'nav.cashier', perm: PERM.PAYMENT_VIEW_PENDING },
  { to: '/cashier/eod', key: 'nav.eod', perm: PERM.CASHIER_EOD_SUBMIT },
  { to: '/approvals', key: 'nav.approvals', perm: PERM.APPROVAL_VIEW_QUEUE },
  { to: '/invoices', key: 'nav.invoices', perm: null },
  { to: '/inventory', key: 'nav.inventory', perm: PERM.INVENTORY_VIEW_STOCK },
  { to: '/inbound', key: 'nav.inbound', perm: PERM.INBOUND_CREATE },
  { to: '/reports', key: 'nav.reports', perm: PERM.REPORT_DAILY_SALES },
  { to: '/settings', key: 'nav.settings', perm: PERM.SETTINGS_BUSINESS_PROFILE },
  { to: '/settings/users', key: 'nav.users', perm: PERM.SETTINGS_USERS_VIEW },
  { to: '/settings/catalog-import', key: 'nav.catalog_import', perm: PERM.SETTINGS_BUSINESS_PROFILE },
];

function NavItem({ to, keyName, perm, onClick }: { to: string; keyName: string; perm: string | null; onClick?: () => void }) {
  const { t } = useTranslation();
  const allowed = perm ? useHasPermission(perm as never) : true;
  if (!allowed) return null;
  return (
    <NavLink
      to={to}
      onClick={onClick}
      className={({ isActive }) =>
        cn(
          // ≥44px touch target
          'block rounded-md px-3 py-3 text-sm font-medium transition',
          isActive ? 'bg-brand-50 text-brand-700' : 'text-slate-700 hover:bg-slate-100 active:bg-slate-200',
        )
      }
    >
      {t(keyName)}
    </NavLink>
  );
}

export default function AppShell() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const [drawerOpen, setDrawerOpen] = useState(false);

  // Close drawer when route changes (mobile UX)
  useEffect(() => { setDrawerOpen(false); }, [location.pathname]);

  // Lock body scroll when drawer open (mobile)
  useEffect(() => {
    if (drawerOpen) document.body.classList.add('overflow-hidden');
    else document.body.classList.remove('overflow-hidden');
    return () => document.body.classList.remove('overflow-hidden');
  }, [drawerOpen]);

  const logout = async () => {
    await supabase.auth.signOut();
    clearPermissionCache();
    clearYardState();
    navigate('/login');
  };

  const toggleLang = () => {
    const next = i18n.language === 'en' ? 'hi' : 'en';
    i18n.changeLanguage(next);
    localStorage.setItem('sega.lang', next);
  };

  // Sidebar content (used by both desktop sidebar and mobile drawer)
  const SidebarBody = ({ onNavClick }: { onNavClick?: () => void }) => (
    <>
      {/* v0.2.24 — visible STAGING banner when running against the staging Supabase project */}
      {import.meta.env.VITE_ENVIRONMENT && import.meta.env.VITE_ENVIRONMENT !== 'production' && (
        <div className="mb-3 rounded-md border-2 border-amber-500 bg-amber-100 px-2 py-1.5 text-center">
          <div className="text-xs font-bold uppercase tracking-wider text-amber-900">
            ⚠ STAGING — {String(import.meta.env.VITE_ENVIRONMENT).toUpperCase()}
          </div>
          <div className="text-[10px] text-amber-700">Test data only · Not real customers</div>
        </div>
      )}
      <div className="mb-6 flex items-center gap-3">
        <img src="/logo.svg" alt="Sega" className="h-10 w-10 rounded" />
        <div className="min-w-0 flex-1">
          <div className="truncate font-semibold text-slate-900">{t('app.name')}</div>
          <YardSwitcher />
        </div>
      </div>
      <nav className="space-y-1">
        {NAV.map((n) => (
          <NavItem key={n.to} to={n.to} keyName={n.key} perm={n.perm} onClick={onNavClick} />
        ))}
      </nav>
      <div className="mt-6 space-y-2 border-t border-slate-200 pt-4">
        <button onClick={toggleLang} className="btn-ghost w-full justify-start py-3 text-left">
          <Languages className="mr-2 h-4 w-4" />
          {i18n.language === 'en' ? 'हिन्दी' : 'English'}
        </button>
        <button onClick={logout} className="btn-ghost w-full justify-start py-3 text-left">
          <LogOut className="mr-2 h-4 w-4" />
          {t('nav.logout')}
        </button>
      </div>
    </>
  );

  return (
    <div className="flex h-screen flex-col md:flex-row">
      {/* Mobile top bar (hidden on md+) */}
      <header className="flex items-center justify-between border-b border-slate-200 bg-white px-3 py-2 md:hidden">
        <button
          onClick={() => setDrawerOpen(true)}
          aria-label="Open menu"
          className="rounded-md p-2 text-slate-700 hover:bg-slate-100 active:bg-slate-200"
        >
          <Menu className="h-6 w-6" />
        </button>
        <div className="flex items-center gap-2">
          <img src="/logo.svg" alt="Sega" className="h-7 w-7 rounded" />
          <span className="text-sm font-semibold text-slate-900">{t('app.name')}</span>
        </div>
        <div className="w-10" /> {/* spacer for symmetry */}
      </header>

      {/* Desktop sidebar (hidden on small) */}
      <aside className="hidden w-64 flex-none border-r border-slate-200 bg-white p-4 md:block md:overflow-y-auto">
        <SidebarBody />
      </aside>

      {/* Mobile drawer */}
      {drawerOpen && (
        <>
          {/* backdrop */}
          <div
            className="fixed inset-0 z-40 bg-slate-900/50 md:hidden"
            onClick={() => setDrawerOpen(false)}
            aria-hidden="true"
          />
          {/* drawer panel */}
          <aside
            className="fixed inset-y-0 left-0 z-50 w-72 max-w-[85%] overflow-y-auto bg-white p-4 shadow-xl md:hidden"
            role="dialog"
            aria-label="Navigation"
          >
            <div className="mb-2 flex justify-end">
              <button
                onClick={() => setDrawerOpen(false)}
                aria-label="Close menu"
                className="rounded-md p-2 text-slate-600 hover:bg-slate-100 active:bg-slate-200"
              >
                <X className="h-5 w-5" />
              </button>
            </div>
            <SidebarBody onNavClick={() => setDrawerOpen(false)} />
          </aside>
        </>
      )}

      {/* Main content */}
      <main className="flex-1 overflow-y-auto p-3 md:p-6">
        <Outlet />
      </main>
    </div>
  );
}
